import {useLocation, useNavigate} from 'react-router-dom';

import {getFeatureFlagValue} from 'infra/http/utils/get-feature-flag-value';
import {FeaturesEnum} from 'presentation/constants';
import {useGetEndpointUrl} from 'presentation/hooks';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useServicePermission} from 'presentation/hooks/useServicePermission';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useOverbookingModalStore} from 'presentation/pages/private/booking/components/Overbooking/store';
import {useBookingConfirmationDrawer} from 'presentation/pages/private/booking/hooks/useBookingConfirmationDrawer';
import {useQueryParams} from 'presentation/pages/private/hooks';
import {useExternalServiceStore} from 'presentation/pages/private/store/externalSerivceUrlStore';

import {useGetHomeQuery} from '../../../../query/useGetHomeQuery';
import {ShortcutAction, ShortCutProps} from '../../types';
import {getBookingTypeByTitle} from '../../utils/getBookingTypeByTitle';
import {
	isFavoriteDoctorsShortcut,
	resolveFlaggedAction,
	resolveShortcutAction,
} from '../../utils/resolveShortcutAction';
import {shouldOpenWarningDrawer} from '../../utils/shouldOpenWarningDrawer';

type UseShortcutClickParams = Pick<
	ShortCutProps,
	| 'title'
	| 'type'
	| 'route'
	| 'endpoint'
	| 'featureFlagName'
	| 'handleClick'
	| 'warningDrawerContent'
	| 'overrideClick'
	| 'setWarningDrawerContainer'
	| 'isBlob'
	| 'disableCache'
>;

export function useShortcutClick({
	title,
	type,
	route,
	endpoint,
	featureFlagName,
	handleClick,
	warningDrawerContent,
	overrideClick,
	setWarningDrawerContainer,
	isBlob,
	disableCache,
}: UseShortcutClickParams) {
	const {clearSearch} = useQueryParams();
	const {canOpenService} = useServicePermission();
	const navigate = useNavigate();
	const {pathname} = useLocation();
	const {isError, setIsError, getLink, isLoading} = useGetEndpointUrl(title);
	const cachedURL = useExternalServiceStore(state => state.urls[title]);
	const setOverbookingModalOpen = useOverbookingModalStore(
		state => state.setOverbookingModalOpen,
	);
	const {setSelectedServiceType, handleOpenBookingConfirmationDrawer} =
		useBookingConfirmationDrawer();
	const setBookingData = useBookingStore(state => state.setBookingData);
	const {data: homeData} = useGetHomeQuery();
	const {isEnabled: isAlertUrgentCareFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_ALERT_URGENT_CARE,
	);

	const selectServiceTypeAndGetSearchParams = () => {
		const searchParams = new URLSearchParams(window.location.search);
		const bookingType = getBookingTypeByTitle(title);

		if (bookingType) setSelectedServiceType(bookingType);

		return searchParams;
	};

	const executeAction = (action: ShortcutAction, searchParams: URLSearchParams) => {
		switch (action.kind) {
			case 'open-cached-url':
				window.open(action.url);
				return;
			case 'fetch-link':
				if (!endpoint) return;
				getLink(endpoint, false, isBlob);
				return;
			case 'open-overbooking':
				setOverbookingModalOpen(true);
				return;
			case 'open-booking-drawer':
				handleOpenBookingConfirmationDrawer();
				return;
			case 'custom-click':
				handleClick?.();
				return;
			case 'navigate':
				navigate(`${action.route}?${searchParams.toString()}`, {
					state: pathname,
					replace: true,
				});
				return;
			default:
				return;
		}
	};

	const handleClickAction = async () => {
		clearSearch();

		if (overrideClick) {
			overrideClick();
			return;
		}

		if (
			isAlertUrgentCareFlagEnabled &&
			warningDrawerContent &&
			shouldOpenWarningDrawer(title, homeData?.appointments)
		) {
			setWarningDrawerContainer?.();
			return;
		}

		setBookingData(prev => ({
			...prev,
			isFavoriteDoctorsFlow: isFavoriteDoctorsShortcut(route, type),
		}));

		if (!canOpenService(type)) return;

		const searchParams = selectServiceTypeAndGetSearchParams();

		const action = resolveShortcutAction({
			type,
			route,
			hasEndpoint: Boolean(endpoint),
			hasHandleClick: Boolean(handleClick),
			hasFeatureFlag: Boolean(featureFlagName),
			cachedURL,
			disableCache,
		});

		if (action.kind === 'check-feature-flag' && featureFlagName) {
			const hasVariation = await getFeatureFlagValue(featureFlagName);

			executeAction(
				resolveFlaggedAction({
					route,
					hasEndpoint: Boolean(endpoint),
					hasHandleClick: Boolean(handleClick),
					hasVariation,
				}),
				searchParams,
			);
			return;
		}

		executeAction(action, searchParams);
	};

	return {handleClickAction, isError, setIsError, isLoading};
}
